import { useState, useEffect } from 'react';
import { sortByName } from '../lib/sortByName';
import { useTranslation } from 'react-i18next';
import { platformValuesAPI, organizationsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import {
    DollarSign,
    Save,
    RotateCcw,
    Building2,
    Info
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '../components/ui/tabs';

const PLATFORMS = [
    { key: 'google_reviews', label: 'Google Review', description: 'Based on industry research, each Google review generates customer value', defaultValue: 7.50 },
    { key: 'tripadvisor', label: 'Tripadvisor Review', description: 'Tripadvisor reviews drive tourism and hospitality', defaultValue: 7.00 },
    { key: 'yelp', label: 'Yelp Review', description: 'Yelp reviews are crucial for local discovery', defaultValue: 6.00 },
    { key: 'trustpilot', label: 'Trustpilot Review', description: 'Trustpilot reviews boost credibility for B2B', defaultValue: 5.00 },
    { key: 'facebook', label: 'Facebook Review', description: 'Facebook reviews help build social proof and trust', defaultValue: 4.00 },
    { key: 'instagram', label: 'Instagram Follow', description: 'Instagram followers contribute to brand awareness', defaultValue: 3.00 }
];

const PlatformValuesSettings = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [organizations, setOrganizations] = useState([]);
    const [selectedOrg, setSelectedOrg] = useState('');
    
    // Default values (user-specified)
    const [defaultValues, setDefaultValues] = useState({
        google_reviews: 7.50,
        tripadvisor: 7.00,
        yelp: 6.00,
        trustpilot: 5.00,
        facebook: 4.00,
        instagram: 3.00
    });
    
    // Org-specific values
    const [orgValues, setOrgValues] = useState(null);
    const [useCustom, setUseCustom] = useState(false);
    const [currency, setCurrency] = useState({ home_currency: 'USD', usd_to_cad_rate: 1.39 });
    const [savingCurrency, setSavingCurrency] = useState(false);
    const [customValues, setCustomValues] = useState({
        google_reviews: 7.50,
        tripadvisor: 7.00,
        yelp: 6.00,
        trustpilot: 5.00,
        facebook: 4.00,
        instagram: 3.00
    });
    
    const { isSuperAdmin } = useAuth();

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        if (selectedOrg) {
            loadOrgValues(selectedOrg);
        }
    }, [selectedOrg]);

    const loadData = async () => {
        try {
            // Load default values
            const defaultsRes = await platformValuesAPI.getDefaults();
            if (defaultsRes.data) {
                setDefaultValues({
                    google_reviews: defaultsRes.data.google_reviews || 7.50,
                    facebook: defaultsRes.data.facebook || 4.00,
                    instagram: defaultsRes.data.instagram || 3.00,
                    trustpilot: defaultsRes.data.trustpilot || 5.00,
                    yelp: defaultsRes.data.yelp || 6.00,
                    tripadvisor: defaultsRes.data.tripadvisor || 6.00
                });
            }
            
            // Load organizations
            const orgsRes = await organizationsAPI.list();
            setOrganizations(sortByName(orgsRes.data));
        } catch (error) {
            toast.error('Failed to load platform values');
        } finally {
            setLoading(false);
        }
    };

    const loadOrgValues = async (orgId) => {
        try {
            platformValuesAPI.getCurrency(orgId)
                .then((r) => setCurrency({
                    home_currency: r.data?.home_currency || 'USD',
                    usd_to_cad_rate: r.data?.usd_to_cad_rate ?? 1.39,
                }))
                .catch(() => setCurrency({ home_currency: 'USD', usd_to_cad_rate: 1.39 }));
            const res = await platformValuesAPI.getOrgValues(orgId);
            setOrgValues(res.data);
            setUseCustom(res.data.is_custom);
            if (res.data.is_custom) {
                setCustomValues({
                    google_reviews: res.data.values.google_reviews || defaultValues.google_reviews,
                    facebook: res.data.values.facebook || defaultValues.facebook,
                    instagram: res.data.values.instagram || defaultValues.instagram,
                    trustpilot: res.data.values.trustpilot || defaultValues.trustpilot,
                    yelp: res.data.values.yelp || defaultValues.yelp,
                    tripadvisor: res.data.values.tripadvisor || defaultValues.tripadvisor
                });
            } else {
                setCustomValues(defaultValues);
            }
        } catch (error) {
            toast.error('Failed to load organization values');
        }
    };

    const saveCurrency = async () => {
        setSavingCurrency(true);
        try {
            await platformValuesAPI.updateCurrency(selectedOrg, {
                home_currency: currency.home_currency,
                usd_to_cad_rate: parseFloat(currency.usd_to_cad_rate) || 1.39,
            });
            toast.success(t('adminSettings.currency.saved', 'Currency settings saved'));
        } catch (error) {
            toast.error(error.response?.data?.detail || t('adminSettings.currency.failed', 'Failed to save currency settings'));
        } finally {
            setSavingCurrency(false);
        }
    };

    const saveDefaultValues = async () => {
        setSaving(true);
        try {
            await platformValuesAPI.updateDefaults(defaultValues);
            toast.success('Default platform values saved');
        } catch (error) {
            toast.error('Failed to save default values');
        } finally {
            setSaving(false);
        }
    };

    const saveOrgValues = async () => {
        if (!selectedOrg) return;
        
        setSaving(true);
        try {
            if (useCustom) {
                await platformValuesAPI.updateOrgValues(selectedOrg, customValues);
                toast.success('Custom values saved for organization');
            } else {
                await platformValuesAPI.resetOrgValues(selectedOrg);
                toast.success('Organization reset to default values');
            }
            loadOrgValues(selectedOrg);
        } catch (error) {
            toast.error('Failed to save organization values');
        } finally {
            setSaving(false);
        }
    };

    const formatCurrency = (value) => {
        return `$${parseFloat(value || 0).toFixed(2)}`;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            </div>
        );
    }

    if (!isSuperAdmin()) {
        return (
            <Card>
                <CardContent className="pt-6 text-center">
                    <p className="text-muted-foreground">Platform value settings are only accessible to Super Admins.</p>
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="space-y-6" data-testid="platform-values-settings">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Platform Values
                </h1>
                <p className="text-muted-foreground mt-1">
                    Configure monetary values for each review platform to calculate ROI
                </p>
            </div>

            <Tabs defaultValue="defaults" className="space-y-6">
                <TabsList>
                    <TabsTrigger value="defaults">Default Values</TabsTrigger>
                    <TabsTrigger value="client">Client-Specific Values</TabsTrigger>
                </TabsList>

                {/* Default Values Tab */}
                <TabsContent value="defaults">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <DollarSign className="w-5 h-5 text-emerald-600" />
                                Default Platform Review Values
                            </CardTitle>
                            <CardDescription>
                                These values apply to all clients unless custom values are set
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {PLATFORMS.map((platform, pi) => (
                                    <div key={platform.key} className="space-y-2">
                                        <Label className="flex items-center gap-2">
                                            {platform.label}
                                            <span className="text-muted-foreground text-xs">(?)</span>
                                        </Label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={defaultValues[platform.key]}
                                                onChange={(e) => setDefaultValues({
                                                    ...defaultValues,
                                                    [platform.key]: parseFloat(e.target.value) || 0
                                                })}
                                                className="pl-7"
                                                data-testid={`default-${platform.key}`}
                                                data-insight-id={pi === 0 ? "platform-values.workflow.default-values" : undefined}
                                                data-insight-label={pi === 0 ? "Platform default review value" : undefined}
                                            />
                                        </div>
                                        <p className="text-xs text-muted-foreground">{platform.description}</p>
                                    </div>
                                ))}
                            </div>

                            <div className="pt-4 border-t flex justify-end">
                                <Button onClick={saveDefaultValues} disabled={saving} data-testid="save-defaults">
                                    <Save className="w-4 h-4 mr-2" />
                                    {saving ? 'Saving...' : 'Save Default Values'}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Info Card */}
                    <Card className="bg-blue-50 border-blue-200">
                        <CardContent className="pt-6">
                            <div className="flex items-start gap-3">
                                <Info className="w-5 h-5 text-blue-500 mt-0.5" />
                                <div>
                                    <h4 className="font-medium text-blue-800 mb-1">{t('adminSettings.howValuesWork')}</h4>
                                    <p className="text-sm text-blue-700">
                                        When calculating ROI, each tap is counted separately by platform. 
                                        The value is calculated by multiplying tap count × platform value. 
                                        These default values are based on industry research but can be customized 
                                        per client to reflect their specific business value.
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Client-Specific Values Tab */}
                <TabsContent value="client">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Building2 className="w-5 h-5 text-purple-600" />
                                Client-Specific Value Overrides
                            </CardTitle>
                            <CardDescription>
                                Set custom platform values for specific organizations
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* Organization Selector */}
                            <div className="max-w-md">
                                <Label className="mb-2 block">Select Organization</Label>
                                <Select value={selectedOrg} onValueChange={setSelectedOrg}>
                                    <SelectTrigger data-testid="select-org">
                                        <SelectValue placeholder="Choose an organization..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {organizations.map(org => (
                                            <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {selectedOrg && (
                                <>
                                    {/* Custom Toggle */}
                                    <div className="flex items-center gap-4 p-4 bg-muted rounded-lg"
                                        data-insight-id="platform-values.workflow.client-override"
                                        data-insight-label="Centralized default vs per-client override"
                                    >
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="radio"
                                                name="valueType"
                                                checked={!useCustom}
                                                onChange={() => setUseCustom(false)}
                                                className="w-4 h-4 text-emerald-600"
                                            />
                                            <span>{t('adminSettings.useDefaultValues')}</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="radio"
                                                name="valueType"
                                                checked={useCustom}
                                                onChange={() => setUseCustom(true)}
                                                className="w-4 h-4 text-emerald-600"
                                            />
                                            <span>{t('adminSettings.useCustomValues')}</span>
                                        </label>
                                        {orgValues?.is_custom && (
                                            <Badge className="bg-purple-100 text-purple-700">Custom values active</Badge>
                                        )}
                                    </div>

                                    {/* Custom Values Form */}
                                    {useCustom && (
                                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                            {PLATFORMS.map(platform => (
                                                <div key={platform.key} className="space-y-2">
                                                    <Label>{platform.label}</Label>
                                                    <div className="relative">
                                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                                                        <Input
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            value={customValues[platform.key]}
                                                            onChange={(e) => setCustomValues({
                                                                ...customValues,
                                                                [platform.key]: parseFloat(e.target.value) || 0
                                                            })}
                                                            className="pl-7"
                                                            data-testid={`custom-${platform.key}`}
                                                        />
                                                    </div>
                                                    <p className="text-xs text-muted-foreground">
                                                        Default: {formatCurrency(defaultValues[platform.key])}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Actions */}
                                    <div className="pt-4 border-t flex justify-between">
                                        <Button 
                                            variant="outline" 
                                            onClick={() => {
                                                setUseCustom(false);
                                                setCustomValues(defaultValues);
                                            }}
                                            data-testid="reset-to-default"
                                        >
                                            <RotateCcw className="w-4 h-4 mr-2" />
                                            Reset to Default
                                        </Button>
                                        <Button onClick={saveOrgValues} disabled={saving} data-testid="save-org-values">
                                            <Save className="w-4 h-4 mr-2" />
                                            {saving ? 'Saving...' : 'Save Values'}
                                        </Button>
                                    </div>
                                </>
                            )}
                        </CardContent>
                    </Card>

                    {/* Display currency — used by the Sales/Calculation Tool.
                        Per-review values above stay stored in the home currency. */}
                    {selectedOrg && (
                        <Card className="mt-4" data-testid="currency-settings-card">
                            <CardHeader>
                                <CardTitle className="text-base flex items-center gap-2">
                                    <DollarSign className="w-4 h-4" />
                                    {t('adminSettings.currency.title', 'Currency')}
                                </CardTitle>
                                <CardDescription>
                                    {t('adminSettings.currency.hint', 'Home currency for stored values, plus the stored USD→CAD rate used to display converted figures.')}
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid gap-4 sm:grid-cols-2">
                                    <div className="space-y-1.5">
                                        <Label>{t('adminSettings.currency.home', 'Home currency')}</Label>
                                        <Select value={currency.home_currency}
                                            onValueChange={(v) => setCurrency((p) => ({ ...p, home_currency: v }))}>
                                            <SelectTrigger data-testid="currency-home-select"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="USD">USD</SelectItem>
                                                <SelectItem value="CAD">CAD</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label>{t('adminSettings.currency.rate', '1 USD = ? CAD')}</Label>
                                        <Input type="number" step="0.01" min="0.1" max="10"
                                            value={currency.usd_to_cad_rate}
                                            onChange={(e) => setCurrency((p) => ({ ...p, usd_to_cad_rate: e.target.value }))}
                                            data-testid="currency-rate-input" />
                                    </div>
                                </div>
                                <div className="flex justify-end">
                                    <Button onClick={saveCurrency} disabled={savingCurrency || !isSuperAdmin()} data-testid="save-currency">
                                        <Save className="w-4 h-4 mr-2" />
                                        {savingCurrency ? t('common.saving', 'Saving...') : t('adminSettings.currency.save', 'Save currency')}
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default PlatformValuesSettings;
